// frontend/src/screens/user/RequestSwapScreen.jsx
import React, { useState, useEffect } from 'react';
import { useLayout } from '../context/LayoutContext';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Button,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Breadcrumbs,
  Link,
  Snackbar,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { NavigateNext, SwapHoriz, Cancel } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useGetMySwapEligibleShiftsQuery,
  useGetSwapCandidatesQuery,
  useCreateSwapRequestMutation,
  useGetMySentSwapRequestsQuery,
  useCancelSwapRequestMutation,
} from '../slices/swapApiSlice';

const formatDateTime = (d) =>
  new Date(d).toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const todayISO = () => new Date().toISOString().split('T')[0];
const plusDaysISO = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
};

// ── Status chip styling for the "My Sent Requests" tab ──────────────────────
const STATUS_CHIP = {
  PendingAcceptance: { label: 'Awaiting a candidate', color: 'info' },
  PendingApproval:   { label: 'Awaiting manager approval', color: 'warning' },
  Approved:          { label: 'Approved', color: 'success' },
  Rejected:          { label: 'Rejected', color: 'error' },
  Cancelled:         { label: 'Cancelled', color: 'default' },
  Expired:           { label: 'Expired', color: 'default' },
};

// ─── Tab 1: Request a Swap ───────────────────────────────────────────────────

const RequestSwapTab = ({ isMobile, showSnackbar, initialShiftId }) => {
  const [startDate, setStartDate] = useState(todayISO());
  // Widen the window when arriving via a specific shift's swap button, so
  // that shift is virtually guaranteed to fall inside the fetched range
  // regardless of how far out it's scheduled. Otherwise default to 6 weeks.
  const [endDate, setEndDate] = useState(initialShiftId ? plusDaysISO(180) : plusDaysISO(42));
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [reason, setReason] = useState('');
  const [appliedDeepLink, setAppliedDeepLink] = useState(false);

  const {
    data: shifts,
    isLoading: loadingShifts,
    error: shiftsError,
  } = useGetMySwapEligibleShiftsQuery({ startDate, endDate });

  // Deep-link support: if we arrived via a "Request Swap" button on a specific
  // shift pill, pre-select that shift once the shift list has loaded. Only
  // runs once per mount — after that, normal manual selection takes over.
  useEffect(() => {
    if (!initialShiftId || appliedDeepLink || !shifts) return;
    const match = shifts.find((s) => s._id === initialShiftId);
    if (match) {
      setSelectedShiftId(match._id);
    } else {
      // Shift exists but isn't in the eligible list (already started, already
      // has an open swap, or outside the current date range) — let the person
      // know rather than silently doing nothing.
      showSnackbar('That shift is no longer available to swap — it may have started, already have a pending swap, or be outside the current date range.', 'warning');
    }
    setAppliedDeepLink(true);
  }, [initialShiftId, appliedDeepLink, shifts, showSnackbar]);

  const {
    data: candidates,
    isLoading: loadingCandidates,
    error: candidatesError,
  } = useGetSwapCandidatesQuery(selectedShiftId, { skip: !selectedShiftId });

  const [createSwapRequest, { isLoading: submitting }] = useCreateSwapRequestMutation();

  const handleSelectShift = (shift) => {
    setSelectedShiftId(shift._id);
    setSelectedCandidateIds([]); // reset candidate picks when the shift changes
  };

  const toggleCandidate = (candidateId) => {
    setSelectedCandidateIds((prev) =>
      prev.includes(candidateId) ? prev.filter((id) => id !== candidateId) : [...prev, candidateId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedShiftId || selectedCandidateIds.length === 0) {
      showSnackbar('Select a shift and at least one candidate first', 'warning');
      return;
    }
    try {
      await createSwapRequest({
        shiftId: selectedShiftId,
        candidateStaffIds: selectedCandidateIds,
        reason,
      }).unwrap();

      showSnackbar('Swap request sent to selected candidates', 'success');
      setSelectedShiftId(null);
      setSelectedCandidateIds([]);
      setReason('');
    } catch (err) {
      showSnackbar(err?.data?.message || err?.error || 'Failed to create swap request', 'error');
    }
  };

  return (
    <>
      {/* Date range filter */}
      <Paper elevation={1} sx={{ p: isMobile ? 1.5 : 2, mb: 2, bgcolor: '#f5f5f5' }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems={isMobile ? 'stretch' : 'center'}>
          <TextField
            label="From"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ bgcolor: 'white' }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ bgcolor: 'white' }}
          />
          <Typography variant="body2" color="text.secondary">
            Showing your upcoming shifts in this range
          </Typography>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        {/* Left panel: my shifts */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Select a shift to swap
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {loadingShifts && (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
            )}

            {shiftsError && (
              <Alert severity="error">Could not load your shifts. Please try again.</Alert>
            )}

            {!loadingShifts && shifts?.length === 0 && (
              <Alert severity="info">No upcoming shifts found in this date range.</Alert>
            )}

            <List dense sx={{ maxHeight: 480, overflowY: 'auto' }}>
              {shifts?.map((shift) => (
                <ListItemButton
                  key={shift._id}
                  selected={selectedShiftId === shift._id}
                  disabled={shift.hasOpenSwapRequest}
                  onClick={() => handleSelectShift(shift)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    border: '1px solid',
                    borderColor: selectedShiftId === shift._id ? 'primary.main' : 'divider',
                  }}
                >
                  <ListItemText
                    primary={formatDateTime(shift.startDateTime)}
                    secondary={
                      <>
                        {shift.wardDepartmentArea} · {shift.categoryOfStaff}
                        {shift.hasOpenSwapRequest && (
                          <Chip label="Swap already pending" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                        )}
                      </>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Right panel: candidates */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Select staff to offer this shift to
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {!selectedShiftId && (
              <Alert severity="info">Pick a shift on the left to see who's eligible to cover it.</Alert>
            )}

            {selectedShiftId && loadingCandidates && (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
            )}

            {candidatesError && (
              <Alert severity="error">Could not load eligible staff for this shift.</Alert>
            )}

            {selectedShiftId && !loadingCandidates && candidates?.length === 0 && (
              <Alert severity="warning">
                No eligible staff found for this shift — everyone is either excluded from this ward,
                already has an overlapping shift, or would breach the 11-hour rest period.
              </Alert>
            )}

            {selectedShiftId && candidates?.length > 0 && (
              <>
                <List dense sx={{ maxHeight: 320, overflowY: 'auto', mb: 2 }}>
                  {candidates.map((c) => (
                    <ListItemButton key={c._id} onClick={() => toggleCandidate(c._id)} dense>
                      <Checkbox
                        edge="start"
                        checked={selectedCandidateIds.includes(c._id)}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ListItemText primary={c.name} secondary={c.jobtitle} />
                    </ListItemButton>
                  ))}
                </List>

                <TextField
                  label="Reason for swap (optional)"
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {selectedCandidateIds.length} staff selected — sent to all at once, first to accept gets the shift
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<SwapHoriz />}
                    disabled={selectedCandidateIds.length === 0 || submitting}
                    onClick={handleSubmit}
                  >
                    {submitting ? 'Sending…' : 'Send Swap Request'}
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </>
  );
};

// ─── Tab 2: My Sent Requests ─────────────────────────────────────────────────

const SentRequestsTab = ({ isMobile, showSnackbar }) => {
  const { data: sentRequests, isLoading, error } = useGetMySentSwapRequestsQuery();
  const [cancelSwapRequest, { isLoading: cancelling }] = useCancelSwapRequestMutation();
  const [confirmCancel, setConfirmCancel] = useState({ open: false, swapRequest: null });

  const handleCancelClick = (swapRequest) => setConfirmCancel({ open: true, swapRequest });

  const handleConfirmCancel = async () => {
    const swapRequest = confirmCancel.swapRequest;
    setConfirmCancel({ open: false, swapRequest: null });
    try {
      await cancelSwapRequest(swapRequest._id).unwrap();
      showSnackbar('Swap request cancelled', 'success');
    } catch (err) {
      showSnackbar(err?.data?.message || err?.error || 'Could not cancel this request', 'error');
    }
  };

  return (
    <Paper elevation={2} sx={{ p: isMobile ? 1.5 : 2 }}>
      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      )}

      {error && <Alert severity="error">Could not load your sent swap requests.</Alert>}

      {!isLoading && sentRequests?.length === 0 && (
        <Alert severity="info">You haven't sent any swap requests yet.</Alert>
      )}

      <List>
        {sentRequests?.map((r, idx) => {
          const statusInfo = STATUS_CHIP[r.status] || { label: r.status, color: 'default' };
          const canCancel = r.status === 'PendingAcceptance';

          return (
            <React.Fragment key={r._id}>
              {idx > 0 && <Divider component="li" />}
              <ListItem
                sx={{ py: 2 }}
                secondaryAction={
                  canCancel && (
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<Cancel />}
                      onClick={() => handleCancelClick(r)}
                      disabled={cancelling}
                    >
                      Cancel
                    </Button>
                  )
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {formatDateTime(r.shiftSnapshot?.startDateTime)}
                      <Chip label={statusInfo.label} size="small" color={statusInfo.color} sx={{ height: 20, fontSize: '0.7rem' }} />
                    </Box>
                  }
                  secondary={
                    <>
                      <Box component="span" sx={{ display: 'block' }}>
                        {r.shiftSnapshot?.wardDepartmentArea} · {r.shiftSnapshot?.categoryOfStaff}
                      </Box>
                      {r.acceptedByName && (
                        <Box component="span" sx={{ display: 'block' }}>
                          {r.status === 'Approved' ? 'Swapped with' : 'Accepted by'}: {r.acceptedByName}
                        </Box>
                      )}
                      {r.managerDecision?.comment && (
                        <Box component="span" sx={{ display: 'block' }}>
                          Manager comment: "{r.managerDecision.comment}"
                        </Box>
                      )}
                      {r.reason && (
                        <Box component="span" sx={{ display: 'block' }}>
                          Your reason: "{r.reason}"
                        </Box>
                      )}
                    </>
                  }
                  sx={{ pr: isMobile ? 0 : 14, mb: isMobile ? 1 : 0 }}
                />
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>

      {/* Confirm cancel dialog */}
      <Dialog open={confirmCancel.open} onClose={() => setConfirmCancel({ open: false, swapRequest: null })}>
        <DialogTitle>Cancel this swap request?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will withdraw the request from everyone it was sent to. The shift will remain assigned to you.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCancel({ open: false, swapRequest: null })}>Keep Request</Button>
          <Button variant="contained" color="error" onClick={handleConfirmCancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ─── Main screen ──────────────────────────────────────────────────────────────

const RequestSwapScreen = () => {
  const { setFluid } = useLayout();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const initialShiftId = searchParams.get('shiftId');

  React.useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  // Tab 0 ("Request a Swap") is the default, which also happens to be
  // correct when arriving with a shiftId from a shift pill's swap button.
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  return (
    <Box sx={{ p: isMobile ? 1.5 : 3 }}>
      <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1.5 }}>
        <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
          Dashboard
        </Link>
        <Typography color="text.primary" variant={isMobile ? 'body2' : 'body1'}>
          Shift Swaps
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <SwapHoriz color="primary" />
        <Typography variant={isMobile ? 'h5' : 'h4'}>Shift Swaps</Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, val) => {
          if (val === 2) {
            // Cross-link tab — navigates to the other swap screen rather than
            // switching local content, since that screen owns its own data.
            navigate('/my-shifts/swap-requests');
            return;
          }
          setActiveTab(val);
        }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Request a Swap" />
        <Tab label="My Sent Requests" />
        <Tab label="Shifts Offered to You" />
      </Tabs>

      {activeTab === 0 && <RequestSwapTab isMobile={isMobile} showSnackbar={showSnackbar} initialShiftId={initialShiftId} />}
      {activeTab === 1 && <SentRequestsTab isMobile={isMobile} showSnackbar={showSnackbar} />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default RequestSwapScreen;